import { Grid, GridRow, Loader } from "grabcad-ui-elements";
import { useCallback, useContext, useEffect } from "react";
import { ApplicationContext, TranslationKey } from "../../components/ApplicationProvider";
import { CenteredGridColumn, EmptyColumn, EqualHeightRow, SideGridColumn } from "./AnalyzeStyle";
import { AnalyzeSelectionProps, AnalyzeType } from "./AnalyzeInterface";
import { useHistory } from "react-router-dom";
import { PATHS } from "../../Routes";
import { useGetAllSaaSPrinters } from "./UseGetAllSaaSPrinters";
import { ComparisonCard } from "./ComparisonCard";
import { useCompanyAsAdminQuery } from "../../graphql";
import { Dimmer } from "semantic-ui-react";
import Parser from "html-react-parser";
import { useCompanyLicensesQuery } from "../../graphql/Queries/Licenses/GetCompanyLicenses";

export type elements = {
    analyze_saas: "ANALYZE_SAAS";
    analyze_on_premise: "ANALYZE_ON_PREMISE";
};

export enum AnalyzeDeploymentType {
    saas = "saas",
    onPrem = "onPrem",
}

const AnalyzeSelection = (props: AnalyzeSelectionProps) => {
    const history = useHistory();
    const { t } = useContext(ApplicationContext);
    const { companyLoading, company } = useCompanyAsAdminQuery();
    const { loadingData, listOfPrinters } = useGetAllSaaSPrinters();
    const { allEnabledFeatureKeys } = useCompanyLicensesQuery();

    // Define translation arrays for SaaS
    const learnMoreSaasSteps: { heading: TranslationKey; description: TranslationKey }[] = [
        {
            heading: "analyze_selection.learn_more_saas.step_1.heading",
            description: "analyze_selection.learn_more_saas.step_1.description",
        },
        {
            heading: "analyze_selection.learn_more_saas.step_2.heading",
            description: "analyze_selection.learn_more_saas.step_2.description",
        },
        {
            heading: "analyze_selection.learn_more_saas.step_3.heading",
            description: "analyze_selection.learn_more_saas.step_3.description",
        },
        {
            heading: "analyze_selection.learn_more_saas.step_4.heading",
            description: "analyze_selection.learn_more_saas.step_4.description",
        },
    ];

    const pointToNoteSaasSteps: { heading: TranslationKey; description: TranslationKey }[] = [
        {
            heading: "analyze_selection.point_to_note_saas.step_1.heading",
            description: "analyze_selection.point_to_note_saas.step_1.description",
        },
        {
            heading: "analyze_selection.point_to_note_saas.step_2.heading",
            description: "analyze_selection.point_to_note_saas.step_2.description",
        },
    ];

    const bestForSaasSteps: TranslationKey[] = [
        "analyze_selection.best_for_saas.step_1",
        "analyze_selection.best_for_saas.step_2",
        "analyze_selection.best_for_saas.step_3",
    ];

    // Define translation arrays for On-Prem
    const learnMoreOnPremSteps: { heading: TranslationKey; description: TranslationKey }[] = [
        {
            heading: "analyze_selection.learn_more_on_prem.step_1.heading",
            description: "analyze_selection.learn_more_on_prem.step_1.description",
        },
        {
            heading: "analyze_selection.learn_more_on_prem.step_2.heading",
            description: "analyze_selection.learn_more_on_prem.step_2.description",
        },
        {
            heading: "analyze_selection.learn_more_on_prem.step_3.heading",
            description: "analyze_selection.learn_more_on_prem.step_3.description",
        },
    ];

    const pointToNoteOnPremSteps: { heading: TranslationKey; description: TranslationKey }[] = [
        {
            heading: "analyze_selection.point_to_note_on_prem.step_1.heading",
            description: "analyze_selection.point_to_note_on_prem.step_1.description",
        },
        {
            heading: "analyze_selection.point_to_note_on_prem.step_2.heading",
            description: "analyze_selection.point_to_note_on_prem.step_2.description",
        },
    ];

    const bestForOnPremSteps: TranslationKey[] = [
        "analyze_selection.best_for_on_prem.step_1",
        "analyze_selection.best_for_on_prem.step_2",
        "analyze_selection.best_for_on_prem.step_3",
    ];

    const navigateToAnalyzePages = useCallback(
        (type: AnalyzeType, isReplace = false) => {
            const method = isReplace ? history.replace : history.push;
            if (type === AnalyzeType.AnalyzeOnPrem) {
                method(PATHS.configureAnalyzeOnPrem);
            } else {
                method({ pathname: PATHS.configureAnalyzeSaaS });
            }
        },
        [history]
    );

    useEffect(() => {
        // Prevent premature navigation
        if (companyLoading) {
            return;
        }

        const hasSaaSUserFeature = allEnabledFeatureKeys.has("analyze_saas_user");

        if (company?.analyzeDeploymentType) {
            if (
                company.analyzeDeploymentType === AnalyzeDeploymentType.saas &&
                hasSaaSUserFeature
            ) {
                navigateToAnalyzePages(AnalyzeType.AnalyzeSaaS, true);
            } else {
                // Covers onPrem or any other value, or if SaaS user feature is missing
                navigateToAnalyzePages(AnalyzeType.AnalyzeOnPrem, true);
            }
        } else if (!hasSaaSUserFeature) {
            // If user doesn't have SaaS user feature and analyzeDeploymentType is falsy
            navigateToAnalyzePages(AnalyzeType.AnalyzeOnPrem, true);
        }

        // All other cases: do nothing (intended for showing comparison page)
    }, [
        companyLoading,
        company?.analyzeDeploymentType,
        company?.analyzeServerAddress,
        navigateToAnalyzePages,
        allEnabledFeatureKeys,
    ]);

    if (companyLoading || loadingData) {
        return (
            <Dimmer active inverted>
                <Loader size="large" inline="centered">
                    Loading
                </Loader>
            </Dimmer>
        );
    } else {
        return (
            <Grid>
                <GridRow columns={3}>
                    <SideGridColumn />
                    <CenteredGridColumn>
                        <h1>{t("analyze_selection.user_name", { user_name: props?.user_name })}</h1>
                        <h3>{t("analyze_selection.select_info")}</h3>
                        <p>{t("analyze_selection.select_note")}</p>
                        <p style={{ fontStyle: "italic", color: "black" }}>
                            {Parser(t("analyze_selection.once_selected"))}
                        </p>
                    </CenteredGridColumn>
                    <SideGridColumn />
                </GridRow>
                <EqualHeightRow stackable columns={4}>
                    <EmptyColumn />
                    {/* SaaS Card */}
                    <ComparisonCard
                        element="ANALYZE_SAAS"
                        logoHeader={t("analyze_selection.analyze_saas")}
                        headerDetails={t("analyze_selection.analyze_saas_tag")}
                        whyChooseText={t("analyze_selection.why_choose_saas")}
                        detailsArray={learnMoreSaasSteps}
                        pointsToNote={pointToNoteSaasSteps}
                        bestForSteps={bestForSaasSteps}
                        disabled={Array.isArray(listOfPrinters) && listOfPrinters.length === 0}
                        loadingData={!!loadingData}
                        navigateToAnalyzePages={navigateToAnalyzePages}
                    />
                    {/* On-Prem Card */}
                    <ComparisonCard
                        element="ANALYZE_ON_PREMISE"
                        logoHeader={t("analyze_selection.analyze_on_prem")}
                        headerDetails={t("analyze_selection.analyze_on_prem_tag")}
                        whyChooseText={t("analyze_selection.why_choose_on_prem")}
                        detailsArray={learnMoreOnPremSteps}
                        pointsToNote={pointToNoteOnPremSteps}
                        bestForSteps={bestForOnPremSteps}
                        disabled={false}
                        navigateToAnalyzePages={navigateToAnalyzePages}
                    />
                    <EmptyColumn />
                </EqualHeightRow>
            </Grid>
        );
    }
};

export default AnalyzeSelection;
